import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Upload, FileText, FileCheck, Trash2, RefreshCw,
  Lock, CheckCircle, AlertCircle, MessageSquare, Clock, XCircle, ImageIcon, Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface KBDocument {
  fileName: string;
  displayName: string;
  uploadedAt: string;
  status: 'ready' | 'indexing' | 'error';
}

interface KBImage {
  id: string;
  r2Key: string;
  displayName: string;
  caption: string;
  uploadedAt: string;
  status: 'captioning' | 'ready' | 'error';
  manifestFileName: string;
}

interface KBEntry {
  fileSearchStoreName: string;
  storeDisplayName: string;
  documents: KBDocument[];
  images: KBImage[];
  createdAt: string;
  systemPrompt?: string;
}

type OverallStatus = 'ready' | 'indexing' | 'error' | 'empty';

function overallStatus(entry: KBEntry | null): OverallStatus {
  if (!entry) return 'empty';
  if (!entry.documents.length && !entry.images?.length) return 'empty';
  if (entry.documents.some(d => d.status === 'indexing')) return 'indexing';
  if (entry.documents.some(d => d.status === 'ready'))    return 'ready';
  if (entry.images?.some(i => i.status === 'ready'))      return 'ready';
  return 'error';
}

interface StatusResponse {
  found: boolean;
  entry: KBEntry | null;
}

const PWD_LS_KEY = 'gemini_kb_admin_password';

export function GeminiKBAdminPage() {
  const { shortCode } = useParams<{ shortCode: string }>();
  const [password, setPassword] = useState(() => localStorage.getItem(PWD_LS_KEY) || '');
  const [isAuthenticated, setIsAuthenticated]   = useState(false);
  const [entry, setEntry]                         = useState<KBEntry | null>(null);
  const [loadingStatus, setLoadingStatus]         = useState(false);
  const [uploading, setUploading]                 = useState(false);
  const [deleting, setDeleting]                   = useState(false);
  const [isDragging, setIsDragging]               = useState(false);
  const [isImageDragging, setIsImageDragging]     = useState(false);
  const [uploadingImage, setUploadingImage]       = useState(false);
  const [editedCaptions, setEditedCaptions]       = useState<Record<string, string>>({});
  const [savingCaption, setSavingCaption]         = useState<string | null>(null);
  const [systemPromptDraft, setSystemPromptDraft] = useState<string>('');
  const [savingPrompt, setSavingPrompt]           = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const showMessage = (type: 'success' | 'error' | 'info', text: string, timeout = 5000) => {
    setMessage({ type, text });
    if (timeout) setTimeout(() => setMessage(null), timeout);
  };

  const fetchStatus = useCallback(async (pwd: string) => {
    if (!shortCode) return;
    setLoadingStatus(true);
    try {
      const res = await fetch(`/api/gemini-kb/${shortCode}/status`, {
        headers: { 'X-Admin-Password': pwd },
      });
      if (res.status === 401) {
        setIsAuthenticated(false);
        showMessage('error', 'Incorrect password');
        return;
      }
      const data = await res.json() as StatusResponse;
      setEntry(data.entry);
      setSystemPromptDraft(data.entry?.systemPrompt ?? '');
      setIsAuthenticated(true);
      localStorage.setItem(PWD_LS_KEY, pwd);
    } catch {
      showMessage('error', 'Failed to connect to server');
    } finally {
      setLoadingStatus(false);
    }
  }, [shortCode]);

  useEffect(() => {
    if (password) fetchStatus(password);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll while any document is still indexing or any image is captioning
  useEffect(() => {
    const busy = entry?.documents.some(d => d.status === 'indexing')
               || entry?.images?.some(i => i.status === 'captioning');
    if (!busy) return;
    const timer = setInterval(() => fetchStatus(password), 4000);
    return () => clearInterval(timer);
  }, [entry, fetchStatus, password]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    fetchStatus(password);
  };

  const uploadFile = async (file: File) => {
    const allowed = ['.txt', '.md', '.csv', '.pdf'];
    if (!allowed.some(ext => file.name.toLowerCase().endsWith(ext))) {
      showMessage('error', 'Unsupported file type. Use .txt, .md, .csv, or .pdf');
      return;
    }

    setUploading(true);
    showMessage('info', 'Uploading and indexing — this may take up to 60 seconds…', 0);
    try {
      const form = new FormData();
      form.append('file', file);

      const res = await fetch(`/api/gemini-kb/${shortCode}/upload`, {
        method: 'POST',
        headers: { 'X-Admin-Password': password },
        body: form,
      });

      const data = await res.json() as { success?: boolean; error?: string; entry?: KBEntry };
      if (!res.ok || data.error) {
        showMessage('error', data.error || 'Upload failed');
        return;
      }

      const lastDoc = data.entry?.documents?.at(-1);
      if (lastDoc?.status === 'ready') {
        showMessage('success', `"${file.name}" uploaded and indexed successfully!`);
      } else {
        showMessage('info', `"${file.name}" uploaded. Indexing in progress — checking in a few seconds…`, 0);
      }
      setEntry(data.entry ?? null);
    } catch {
      showMessage('error', 'Upload failed — check your connection');
    } finally {
      setUploading(false);
    }
  };

  const uploadImage = async (file: File) => {
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      showMessage('error', 'Unsupported image type. Use JPEG, PNG, WEBP, or GIF.');
      return;
    }
    setUploadingImage(true);
    showMessage('info', 'Uploading image and generating caption…', 0);
    try {
      const form = new FormData();
      form.append('file', file);
      const res  = await fetch(`/api/gemini-kb/${shortCode}/upload-image`, {
        method: 'POST',
        headers: { 'X-Admin-Password': password },
        body: form,
      });
      const data = await res.json() as { success?: boolean; error?: string; entry?: KBEntry };
      if (data.entry) setEntry(data.entry);
      if (!res.ok || data.error) { showMessage('error', data.error || 'Image upload failed'); return; }
      showMessage('success', `"${file.name}" uploaded and captioned!`);
      setEntry(data.entry ?? null);
    } catch {
      showMessage('error', 'Image upload failed — check your connection');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await uploadImage(file);
    e.target.value = '';
  };

  const handleImageDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsImageDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await uploadImage(file);
  };

  const handleSaveCaption = async (img: KBImage) => {
    const newCaption = editedCaptions[img.id];
    if (!newCaption || newCaption === img.caption) return;
    setSavingCaption(img.id);
    try {
      const res  = await fetch(`/api/gemini-kb/${shortCode}/image/${img.id}/caption`, {
        method: 'PATCH',
        headers: { 'X-Admin-Password': password, 'Content-Type': 'application/json' },
        body: JSON.stringify({ caption: newCaption }),
      });
      const data = await res.json() as { success?: boolean; error?: string; entry?: KBEntry };
      if (!res.ok || data.error) { showMessage('error', data.error || 'Failed to save caption'); return; }
      showMessage('success', 'Caption saved and re-indexed');
      setEntry(data.entry ?? null);
      setEditedCaptions(prev => { const n = { ...prev }; delete n[img.id]; return n; });
    } catch {
      showMessage('error', 'Failed to save caption');
    } finally {
      setSavingCaption(null);
    }
  };

  const handleDeleteImage = async (img: KBImage) => {
    if (!confirm(`Remove image "${img.displayName}"?`)) return;
    try {
      const res  = await fetch(`/api/gemini-kb/${shortCode}/image/${img.id}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Password': password },
      });
      const data = await res.json() as { success?: boolean; error?: string; entry?: KBEntry };
      if (!res.ok || data.error) { showMessage('error', data.error || 'Failed to delete image'); return; }
      showMessage('success', `"${img.displayName}" removed`);
      setEntry(data.entry ?? null);
    } catch {
      showMessage('error', 'Delete failed — check your connection');
    }
  };

  const handleSaveSystemPrompt = async () => {
    setSavingPrompt(true);
    try {
      const res = await fetch(`/api/gemini-kb/${shortCode}/system-prompt`, {
        method: 'PATCH',
        headers: { 'X-Admin-Password': password, 'Content-Type': 'application/json' },
        body: JSON.stringify({ systemPrompt: systemPromptDraft }),
      });
      const data = await res.json() as { success?: boolean; error?: string; entry?: KBEntry };
      if (!res.ok || data.error) { showMessage('error', data.error || 'Failed to save'); return; }
      setEntry(data.entry ?? null);
      showMessage('success', 'System prompt saved');
    } catch {
      showMessage('error', 'Failed to save system prompt');
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await uploadFile(file);
    e.target.value = '';
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await uploadFile(file);
  };

  const handleDeleteFile = async (doc: KBDocument) => {
    if (!confirm(`Remove "${doc.displayName}" from the knowledge base?`)) return;
    try {
      const res = await fetch(
        `/api/gemini-kb/${shortCode}/file/${encodeURIComponent(doc.fileName)}`,
        { method: 'DELETE', headers: { 'X-Admin-Password': password } },
      );
      const data = await res.json() as { success?: boolean; error?: string; entry?: KBEntry };
      if (!res.ok || data.error) {
        showMessage('error', data.error || 'Failed to delete file');
        return;
      }
      showMessage('success', `"${doc.displayName}" removed`);
      setEntry(data.entry ?? null);
    } catch {
      showMessage('error', 'Delete failed — check your connection');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Delete the knowledge base for this shortcode? Users will no longer be able to chat.')) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/gemini-kb/${shortCode}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Password': password },
      });
      if (res.ok) {
        showMessage('success', 'Knowledge base deleted');
        setEntry(null);
      } else {
        showMessage('error', 'Failed to delete');
      }
    } catch {
      showMessage('error', 'Delete failed — check your connection');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString();
  };

  const docStatusBadge = (status: KBDocument['status']) => {
    if (status === 'ready')    return <span className="text-xs font-medium text-green-700 bg-green-100 rounded-full px-2 py-0.5">Ready</span>;
    if (status === 'indexing') return <span className="text-xs font-medium text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">Indexing…</span>;
    return <span className="text-xs font-medium text-red-700 bg-red-100 rounded-full px-2 py-0.5">Error</span>;
  };

  // ── Login screen ────────────────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-md w-full max-w-sm p-8">
          <div className="flex items-center gap-3 mb-2">
            <Lock className="h-6 w-6 text-indigo-600" />
            <h1 className="text-xl font-semibold">KB Admin</h1>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            Short code: <span className="font-mono text-gray-700">{shortCode}</span>
          </p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Password</label>
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter admin password"
                autoFocus
              />
            </div>
            {message && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" /> {message.text}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={!password || loadingStatus}>
              {loadingStatus ? 'Checking…' : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // ── Main admin UI ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Knowledge Base Admin</h1>
            <p className="text-sm text-gray-500 mt-1">
              Short code: <span className="font-mono">{shortCode}</span>
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => fetchStatus(password)} disabled={loadingStatus}>
            <RefreshCw className={`h-4 w-4 ${loadingStatus ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Toast */}
        {message && (
          <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200'
            : message.type === 'info'  ? 'bg-blue-50 text-blue-800 border border-blue-200'
            : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.type === 'success' ? <CheckCircle className="h-4 w-4 flex-shrink-0" />
            : message.type === 'info'   ? <Clock className="h-4 w-4 flex-shrink-0" />
            : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
            {message.text}
          </div>
        )}

        {/* Status card */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Documents ({entry?.documents?.length ?? 0})
            </h2>
            {entry && (
              <Button
                variant="outline" size="sm"
                onClick={handleDelete} disabled={deleting}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {deleting ? 'Deleting…' : 'Delete all'}
              </Button>
            )}
          </div>

          {entry && entry.documents.length > 0 ? (
            <div className="space-y-2">
              {entry.documents.map((doc) => (
                <div key={doc.fileName || doc.displayName} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-gray-50 border">
                  <div className="flex items-center gap-3 min-w-0">
                    {doc.status === 'ready'
                      ? <FileCheck className="h-5 w-5 text-green-500 flex-shrink-0" />
                      : doc.status === 'error'
                        ? <XCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                        : <FileText className="h-5 w-5 text-amber-400 flex-shrink-0" />
                    }
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">{doc.displayName}</p>
                      <p className="text-xs text-gray-500">{formatDate(doc.uploadedAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {docStatusBadge(doc.status)}
                    <button
                      onClick={() => handleDeleteFile(doc)}
                      disabled={!doc.fileName}
                      title="Remove this file"
                      className="p-1 rounded text-gray-400 hover:text-red-500 disabled:opacity-30"
                    >
                      <XCircle className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}

              {overallStatus(entry) === 'ready' && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm text-gray-500 mb-2">Share the chat link with users:</p>
                  <Link
                    to={`/kb-chat/${shortCode}`}
                    target="_blank"
                    className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-800"
                  >
                    <MessageSquare className="h-4 w-4" />
                    /kb-chat/{shortCode}
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 text-gray-500">
              <FileText className="h-8 w-8 text-gray-300" />
              <p className="text-sm">No documents uploaded yet.</p>
            </div>
          )}
        </div>

        {/* Images card */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
            Images ({entry?.images?.length ?? 0})
          </h2>

          {entry && entry.images && entry.images.length > 0 ? (
            <div className="space-y-4 mb-4">
              {entry.images.map((img) => {
                const captionVal = editedCaptions[img.id] ?? img.caption;
                const isDirty    = captionVal !== img.caption;
                return (
                  <div key={img.id} className="border rounded-lg overflow-hidden">
                    <div className="flex gap-3 p-3">
                      {/* Thumbnail */}
                      <div className="w-20 h-20 flex-shrink-0 rounded overflow-hidden bg-gray-100">
                        <img
                          src={`/api/gemini-kb/${shortCode}/image/${img.id}`}
                          alt={img.displayName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{img.displayName}</p>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {img.status === 'captioning'
                              ? <span className="text-xs font-medium text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">Captioning…</span>
                              : img.status === 'error'
                                ? <span className="text-xs font-medium text-red-700 bg-red-100 rounded-full px-2 py-0.5">Error</span>
                                : <span className="text-xs font-medium text-green-700 bg-green-100 rounded-full px-2 py-0.5">Ready</span>
                            }
                            <button
                              onClick={() => handleDeleteImage(img)}
                              title="Remove image"
                              className="p-1 rounded text-gray-400 hover:text-red-500"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400">{formatDate(img.uploadedAt)}</p>
                      </div>
                    </div>
                    {/* Caption editor */}
                    <div className="border-t px-3 pb-3 pt-2 bg-gray-50">
                      <div className="flex items-start gap-2">
                        <Pencil className="h-3.5 w-3.5 text-gray-400 mt-2 flex-shrink-0" />
                        <textarea
                          value={captionVal}
                          onChange={e => setEditedCaptions(prev => ({ ...prev, [img.id]: e.target.value }))}
                          placeholder={img.status === 'captioning' ? 'Generating caption…' : 'Enter a caption for this image'}
                          disabled={img.status === 'captioning'}
                          rows={3}
                          className="flex-1 text-xs text-gray-700 bg-white border border-gray-200 rounded p-2 resize-none focus:outline-none focus:border-indigo-400 disabled:opacity-50"
                        />
                      </div>
                      {isDirty && (
                        <div className="flex justify-end mt-2">
                          <Button
                            size="sm"
                            onClick={() => handleSaveCaption(img)}
                            disabled={!!savingCaption}
                          >
                            {savingCaption === img.id ? 'Saving…' : 'Save caption'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center gap-3 text-gray-500 mb-4">
              <ImageIcon className="h-8 w-8 text-gray-300" />
              <p className="text-sm">No images uploaded yet.</p>
            </div>
          )}

          {/* Image upload */}
          <div
            onDragOver={e => { e.preventDefault(); setIsImageDragging(true); }}
            onDragLeave={e => { e.preventDefault(); setIsImageDragging(false); }}
            onDrop={handleImageDrop}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              uploadingImage    ? 'border-indigo-300 bg-indigo-50'
              : isImageDragging ? 'border-indigo-500 bg-indigo-50'
              : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <ImageIcon className={`h-7 w-7 mx-auto mb-2 ${isImageDragging ? 'text-indigo-500' : 'text-gray-400'}`} />
            <p className="text-xs text-gray-500 mb-2">
              {uploadingImage ? 'Uploading and captioning…' : 'JPEG, PNG, WEBP, or GIF'}
            </p>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleImageSelect}
              className="hidden"
              id="kb-image-upload"
              disabled={uploadingImage}
            />
            <label htmlFor="kb-image-upload">
              <Button type="button" variant="outline" size="sm" disabled={uploadingImage} asChild>
                <span>
                  <Upload className="h-3.5 w-3.5 mr-1.5" />
                  {uploadingImage ? 'Uploading…' : 'Select Image'}
                </span>
              </Button>
            </label>
          </div>
        </div>

        {/* System prompt */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">System Prompt</h2>
          <p className="text-xs text-gray-400 mb-3">
            Optional custom persona / context for this KB. Leave blank to use the default.
          </p>
          <textarea
            value={systemPromptDraft}
            onChange={e => setSystemPromptDraft(e.target.value)}
            placeholder={`You are a friendly assistant for a hair salon named Sakura. Help trainees understand techniques using the uploaded training materials.`}
            rows={5}
            className="w-full text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3 resize-y focus:outline-none focus:border-indigo-400"
          />
          <div className="flex justify-end mt-2">
            <Button
              size="sm"
              onClick={handleSaveSystemPrompt}
              disabled={savingPrompt || systemPromptDraft === (entry?.systemPrompt ?? '')}
            >
              {savingPrompt ? 'Saving…' : 'Save prompt'}
            </Button>
          </div>
        </div>

        {/* Upload area */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            Add Document
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Upload a plain text, Markdown, CSV, or PDF file. Each upload adds to the store — multiple documents are supported.
            Indexing takes up to 60 seconds.
          </p>

          <div
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={e => { e.preventDefault(); setIsDragging(false); }}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors ${
              uploading      ? 'border-indigo-300 bg-indigo-50'
              : isDragging   ? 'border-indigo-500 bg-indigo-50'
              : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <Upload className={`h-10 w-10 mx-auto mb-3 ${isDragging ? 'text-indigo-500' : 'text-gray-400'}`} />
            <p className="text-sm text-gray-600 mb-3">
              {uploading ? 'Uploading and indexing…' : 'Drag and drop a file here, or click to select'}
            </p>
            <input
              type="file"
              accept=".txt,.md,.csv,.pdf,text/plain,text/markdown,text/csv,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
              id="kb-upload"
              disabled={uploading}
            />
            <label htmlFor="kb-upload">
              <Button type="button" variant="outline" disabled={uploading} asChild>
                <span>
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? 'Uploading…' : 'Select File'}
                </span>
              </Button>
            </label>
          </div>
        </div>

        {/* Sign out */}
        <div className="text-center">
          <button
            className="text-sm text-gray-400 hover:text-gray-600"
            onClick={() => {
              localStorage.removeItem(PWD_LS_KEY);
              setIsAuthenticated(false);
              setPassword('');
            }}
          >
            Sign out
          </button>
        </div>

      </div>
    </div>
  );
}
